import React, { useEffect } from 'react';
import { MdSportsSoccer } from 'react-icons/md';
import { FaRunning, FaTimes, FaMapMarkerAlt, FaClock, FaInfoCircle, FaUsers } from 'react-icons/fa';
import '../../scss/calendar/calendarEventModal.scss';

const CalendarEventModal = ({ event, onClose, onRespond }) => {
    useEffect(() => {
        // Add class to body when modal opens
        document.body.classList.add('modal-open');
        
        // Remove class when modal closes
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    const isUpcoming = new Date(event.match_date) > new Date().setHours(0,0,0,0);

    const getEventIcon = (type) => {
        switch (type) {
            case 'match':
                return <MdSportsSoccer className="event-icon match" />;
            case 'training':
                return <FaRunning className="event-icon training" />;
            default:
                return null;
        }
    };

    const calculateEndTime = (startTime, duration) => {
        if (!startTime) return '';
        
        const [hours, minutes] = startTime.split(':');
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0);
        date.setMinutes(date.getMinutes() + parseInt(duration || 60));
        
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).toLowerCase();
    };

    const formatTimeRange = (startTime, duration, eventType) => {
        if (!startTime) return '';
        
        // Convert 24h start time to 12h format
        const [hours, minutes] = startTime.split(':');
        const startDate = new Date();
        startDate.setHours(parseInt(hours), parseInt(minutes), 0);
        const formattedStartTime = startDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).toLowerCase();
        
        // For training events, show start and end time
        if (eventType === 'training') {
            const endTime = calculateEndTime(startTime, duration);
            return `${formattedStartTime} - ${endTime}`;
        }
        
        // For matches, only show start time (kick-off)
        return formattedStartTime;
    };

    const handleResponse = (response) => {
        onRespond(event.match_id, response);
    };

    const hasStatus = event.status && (event.status === 'cancelled' || event.status === 'postponed');

    return (
        <div className="calendar-event-modal-overlay" onClick={onClose}>
            <div className={`calendar-event-modal ${event.type} ${event.status || ''}`} onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>
                    <FaTimes />
                </button>
                
                {(event.status === 'cancelled' || event.status === 'postponed') && (
                    <div className={`status-banner ${event.status}`}>
                        {event.status === 'cancelled' ? 'Cancelled' : 
                         event.status === 'postponed' ? 'Postponed' : ''}
                    </div>
                )}
                
                <div className="event-content">
                    <div className="event-header">
                        {getEventIcon(event.type)}
                        <h3>{event.type === 'match' ? 'Match Day' : 'Training'}</h3>
                    </div>
                    <div className="event-info">
                        <div className="info-item">
                            <FaMapMarkerAlt />
                            <span>{event.location}</span>
                        </div>
                        <div className="info-item">
                            <FaClock />
                            <span>{formatTimeRange(event.match_time, event.duration, event.type)}</span>
                        </div>
                        {event.opposition && (
                            <div className="info-item">
                                <FaUsers />
                                Vs. <span>{event.opposition}</span>
                            </div>
                        )}
                        {event.match_type && (
                            <div className="info-item">
                                <FaInfoCircle />
                                <span className="match-type">{event.match_type}</span>
                            </div>
                        )}
                        {event.instructions && (
                            <div className='info-item'>
                                <FaInfoCircle />
                                <span style={{ color: '#6d6b6b' }}><em>{event.instructions}</em></span>
                            </div>
                        )}
                        {hasStatus && event.last_update_message && (
                            <div className="info-item status-reason">
                                <FaInfoCircle />
                                <span>{event.last_update_message}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarEventModal; 